import { getApiBaseUrl } from "../utils/ssoProvider";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/axiosInstance.js";

const API_URL = getApiBaseUrl();

// Policy topic/score data, shared between PoliciesPage's own gauge + breakdown UI and the
// globally-rendered DocumentPreviewModal (see components/policy/GlobalDocumentPreviewModal.jsx).
// Lives in Redux so the preview modal has real data to show even when opened from a page
// other than PoliciesPage.
export const fetchPolicyScores = createAsyncThunk("policyScore/fetchPolicyScores", async () => {
  const response = await axiosInstance.get(`${API_URL}/v1/store/documents/policy-scores`);
  const topics = response.data?.topics ?? [];
  return {
    topics,
    // "others" has no template to score against (score is always null) — exclude
    // it from the Score Breakdown widget; it still appears elsewhere via topics.
    topicScoresObj: Object.fromEntries(topics.filter(t => t.key !== "others").map(t => [t.key, t.score])),
    overallScore: response.data?.overall_score ?? null,
  };
});

// Shared by both "Apply" buttons in DocumentPreviewModal (Suggested Improvements and
// Issues & Questions) — same endpoint, same payload shape, just a different source list
// of answers built by the caller.
export const applyPolicyFeedbacks = createAsyncThunk(
  "policyScore/applyPolicyFeedbacks",
  async ({ sectionKey, answers }, { rejectWithValue }) => {
    try {
      await axiosInstance.post(`${API_URL}/v1/store/documents/policy-scores/${sectionKey}/apply`, { answers });
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || "Failed to apply improvements");
    }
  }
);

// Dismiss one gap or feedback. Both are rows in the same backing table, so the
// item's `id` is all the endpoint needs — the caller doesn't say which card it
// came from. Hiding lasts until the next scoring run: if the problem is still
// there it comes back, so this is "not now", not "never".
export const ignorePolicyItem = createAsyncThunk(
  "policyScore/ignorePolicyItem",
  async ({ itemId, ignored = true }, { rejectWithValue }) => {
    try {
      await axiosInstance.post(`${API_URL}/v1/store/documents/policy-items/${itemId}/ignore?ignored=${ignored}`);
      return { itemId, ignored };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || "Failed to ignore item");
    }
  }
);

const policyScoreSlice = createSlice({
  name: "policyScore",
  initialState: {
    policyTopicScores: {},
    policyTopics: [],
    overallScore: null,
    isLoadingScores: true,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchPolicyScores.fulfilled, (state, action) => {
        state.policyTopicScores = action.payload.topicScoresObj;
        state.policyTopics = action.payload.topics;
        state.overallScore = action.payload.overallScore;
        state.isLoadingScores = false;
      })
      .addCase(fetchPolicyScores.rejected, state => {
        // Silently fail — breakdown shows "--" scores if unavailable
        state.isLoadingScores = false;
      });
  },
});

export default policyScoreSlice.reducer;
